import moment from "moment";
import { ArrowLineDown, CaretLeft } from "@phosphor-icons/react";
import useTransactionDetatails from "../../../hooks/useTransactionDetails";
import { Link, useLocation, useParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { formatAsMoney } from "../../../helpers/numbers";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { getTransactionIcon } from "../../../helpers/transaction";
import logo from "../../../assets/images/visaro-logo-avatar.png";
import domtoimage from "dom-to-image";
import type { NormalizedTransaction } from "../../../hooks/useAccountTransactions";
import {
  useCreateCaseTicket,
  useCreateCaseDispute,
} from "../../../hooks/useCaseManagementActions";
import { toast } from "sonner";

/** Receipt data shape used for rendering (from API or from list state). */
type ReceiptData = {
  id: string;
  description: string;
  amount: number | string;
  currency_code: string;
  status: string;
  reference: string;
  created_at: string;
  type: string;
  note?: string;
  phone_number?: string;
  service_type?: string;
  data_bundle?: { plan_name?: string };
};

function fromNormalizedTransaction(tx: NormalizedTransaction): ReceiptData {
  return {
    id: tx.id,
    description: tx.description,
    amount: tx.amount,
    currency_code: "NGN",
    status: tx.status,
    reference: tx.id,
    created_at: tx.date,
    type: tx.type,
  };
}

function fromApiData(d: any): ReceiptData | null {
  if (!d) return null;
  const amount = typeof d.amount === "string" ? parseFloat(d.amount) : d.amount;
  return {
    id: d.id,
    description: d.description ?? `Transaction - ${d.reference}`,
    amount: Number.isFinite(amount) ? amount : 0,
    currency_code: d.currency_code ?? "NGN",
    status: d.status ?? "successful",
    reference: d.reference ?? d.id,
    created_at: d.created_at ?? "",
    type: d.type ?? "",
    note: d.note,
    phone_number: d.phone_number,
    service_type: d.service_type,
    data_bundle: d.data_bundle,
  };
}

const TransactionDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const stateTx = location.state?.transaction as NormalizedTransaction | undefined;

  const { transactionDetails, isLoading } = useTransactionDetatails(id, {
    enabled: !!id && !stateTx,
  });

  const receipt = useMemo((): ReceiptData | null => {
    if (stateTx) return fromNormalizedTransaction(stateTx);
    if (transactionDetails?.data) return fromApiData(transactionDetails.data);
    return null;
  }, [stateTx, transactionDetails?.data]);

  const icon = useMemo(
    () => getTransactionIcon(receipt?.type ?? ""),
    [receipt?.type]
  );
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [reasonCode, setReasonCode] = useState("UNAUTHORIZED_DEBIT");
  const [reasonText, setReasonText] = useState("");

  const { createCaseTicketAsync, isCreatingTicket } = useCreateCaseTicket();
  const { createCaseDisputeAsync, isCreatingDispute } = useCreateCaseDispute();

  const handleDownload = useCallback(() => {
    const el = document.getElementById("reciept");
    if (!el || !receipt) return;
    domtoimage
      .toPng(el as Node, {
        quality: 0.99,
        width: el.clientWidth * 2,
        height: el.clientHeight * 2,
        style: {
          transform: "scale(2)",
          transformOrigin: "top left",
        },
      })
      .then(function (dataUrl) {
        const link = document.createElement("a");
        link.download = `Transaction-${receipt.reference}.png`;
        link.href = dataUrl;
        link.click();
      });
  }, [receipt]);

  const handleCreateDispute = async (e: FormEvent) => {
    e.preventDefault();
    if (!receipt) return;

    try {
      const amount =
        typeof receipt.amount === "number"
          ? receipt.amount
          : parseFloat(String(receipt.amount)) || undefined;

      const ticketRes = await createCaseTicketAsync({
        category: "PAYMENT",
        subcategory: "TRANSACTION_DISPUTE",
        subject: `Transaction dispute: ${receipt.reference || receipt.id}`,
        description: reasonText.trim()
          ? reasonText.trim()
          : `Dispute raised for transaction ${receipt.reference || receipt.id}`,
        channel: "WEB",
        priority: "MEDIUM",
      });

      const ticketId = ticketRes?.data?.id;
      if (!ticketId) {
        throw new Error("Unable to create support ticket for this dispute");
      }

      await createCaseDisputeAsync({
        ticketId,
        transactionId: receipt.reference || receipt.id,
        amount,
        currency: receipt.currency_code || "NGN",
        reasonCode,
        reasonText: reasonText.trim() || undefined,
      });

      toast.success("Dispute created and sent to support");
      setReasonText("");
      setShowDisputeForm(false);
    } catch (err: any) {
      toast.error(err?.message || "Failed to create dispute");
    }
  };

  const showLoading = !stateTx && isLoading;
  const showReceipt = receipt && !showLoading;
  const showNotFound = !showLoading && !receipt && !isLoading;

  return (
    <>
      {showLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      <div className="w-full flex flex-col gap-5 px-5 py-5">
        <Link
          to="/payments/history"
          className="inline-flex items-center gap-2 text-deepBlue font-medium hover:underline"
        >
          <CaretLeft weight="bold" size={20} />
          Back to transaction history
        </Link>

        {showReceipt && (
          <div className="flex flex-col gap-5 items-center">
            <div id="reciept" className="w-full max-w-xl">
              <div className="border border-gray-300 bg-white w-full mx-auto p-5 rounded-lg flex flex-col items-center gap-5">
                <div className="flex items-center gap-2 font-medium text-gray-700 w-full">
                  <img
                    src={logo}
                    alt="Visaro"
                    className="w-7 border border-gray-200 rounded-md"
                  />
                  <div className="flex-1">Transaction receipt</div>
                  <div>
                    {receipt.created_at
                      ? moment(receipt.created_at).format("LL")
                      : "—"}
                  </div>
                </div>
                <div
                  className="p-5 rounded-full"
                  style={{ backgroundColor: icon.color + "20" }}
                >
                  <icon.icon size={42} color={icon.color} />
                </div>
                <div className="text-center flex flex-col gap-2">
                  <div className="text-lg font-medium capitalize">
                    {receipt.description}
                  </div>
                  <div className="font-semibold text-2xl">
                    {receipt.currency_code}{" "}
                    {formatAsMoney(
                      typeof receipt.amount === "number"
                        ? receipt.amount
                        : parseFloat(String(receipt.amount)) || 0
                    )}
                  </div>
                </div>
                <div className="w-full flex flex-col gap-5">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="text-gray-700">Status</div>
                    <div
                      className={`capitalize p-1 rounded-md px-2 font-medium ${
                        receipt.status === "failed"
                          ? "bg-red-100 text-red-500"
                          : receipt.status === "successful" ||
                              receipt.status === "credit"
                            ? "bg-green-100 text-green-500"
                            : "bg-amber-100 text-amber-500"
                      }`}
                    >
                      {receipt.status}
                    </div>
                  </div>
                  {receipt.note && (
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="text-gray-700">Note</div>
                      <div>{receipt.note}</div>
                    </div>
                  )}
                  {receipt.phone_number && (
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="text-gray-700">Phone Number</div>
                      <div>{receipt.phone_number}</div>
                    </div>
                  )}
                  {receipt.service_type && (
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="text-gray-700">Service</div>
                      <div className="capitalize">{receipt.service_type}</div>
                    </div>
                  )}
                  {receipt.data_bundle?.plan_name && (
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="text-gray-700">Bundle</div>
                      <div className="capitalize">
                        {receipt.data_bundle.plan_name}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="text-gray-700">Reference</div>
                    <div>{receipt.reference}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="text-gray-700">Date</div>
                    <div>
                      {receipt.created_at
                        ? moment(receipt.created_at).format("LLL")
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleDownload}
              className="p-2 py-3 px-10 border-2 border-gray-300 rounded-xl font-medium flex items-center gap-2 hover:bg-gray-50"
            >
              <ArrowLineDown weight="bold" size={20} />
              Download receipt
            </button>

            <button
              onClick={() => setShowDisputeForm((prev) => !prev)}
              className="p-2 py-3 px-10 border-2 border-visaro-orange text-visaro-orange rounded-xl font-medium hover:bg-orange-50"
            >
              {showDisputeForm ? "Cancel issue report" : "Report an issue"}
            </button>

            {showDisputeForm && (
              <form
                onSubmit={handleCreateDispute}
                className="w-full max-w-xl p-4 border rounded-lg bg-white flex flex-col gap-3"
              >
                <div className="font-semibold">Raise dispute for this transaction</div>
                <select
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}
                  className="border rounded-md p-2 text-sm"
                >
                  <option value="UNAUTHORIZED_DEBIT">UNAUTHORIZED_DEBIT</option>
                  <option value="DUPLICATE_CHARGE">DUPLICATE_CHARGE</option>
                  <option value="CARD_NOT_PRESENT">CARD_NOT_PRESENT</option>
                  <option value="FAILED_REVERSAL">FAILED_REVERSAL</option>
                  <option value="OTHER">OTHER</option>
                </select>
                <textarea
                  rows={4}
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Describe the issue"
                  className="border rounded-md p-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={isCreatingTicket || isCreatingDispute}
                  className="bg-visaro-orange text-white px-4 py-2 rounded-md text-sm disabled:opacity-60"
                >
                  {isCreatingTicket || isCreatingDispute ? "Submitting..." : "Submit dispute"}
                </button>
              </form>
            )}
          </div>
        )}

        {showNotFound && (
          <div className="py-10 px-5 text-center">
            <p className="text-gray-600 font-medium">Transaction not found</p>
            <p className="text-sm text-gray-500 mt-1">
              This transaction may not exist or you don&apos;t have access to it.
              Open it from the transaction history list to view details.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default TransactionDetails;
